import axios from 'axios';

// Base axios configuration
const apiClient = axios.create({
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for global error handling
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

/**
 * Generic function to perform HTTP requests
 * @param {string} method - HTTP method: 'GET', 'POST', 'PUT', 'DELETE'
 * @param {string} url - Endpoint URL
 * @param {Object} data - Data to send (optional, for POST/PUT)
 * @param {Object} config - Additional axios configuration (optional)
 * @returns {Promise} - Promise with server response
 */
export const baseRequest = async (method, url, data = null, config = {}) => {
  try {
    const requestConfig = {
      method: method.toUpperCase(),
      url,
      ...config,
    };

    // Add data only for methods that require it
    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
      requestConfig.data = data;
    }

    const response = await apiClient(requestConfig);
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Specific methods for each type of request
 */
export const get = (url, config = {}) => {
  return baseRequest('GET', url, null, config);
};

export const post = (url, data, config = {}) => {
  return baseRequest('POST', url, data, config);
};

export const put = (url, data, config = {}) => {
  return baseRequest('PUT', url, data, config);
};

export const update = (url, data, config = {}) => {
  return baseRequest('PUT', url, data, config);
};

export const patch = (url, data, config = {}) => {
  return baseRequest('PATCH', url, data, config);
};

export const del = (url, config = {}) => {
  return baseRequest('DELETE', url, null, config);
};

// Alias for delete (since delete is a reserved word)
export const deleteRequest = del;

// Also export the configured axios client in case additional customization is needed
export { apiClient };

const baseRequestModule = {
  baseRequest,
  get,
  post,
  put,
  update,
  patch,
  delete: del,
  deleteRequest,
  apiClient,
};

export default baseRequestModule;

/*
USAGE EXAMPLES:

// Import the functions
import { get, post, put, deleteRequest, baseRequest } from './utils/baseRequest';

// 1. GET Request
const fetchUsers = async () => {
  try {
    const users = await get('https://api.example.com/users');
    console.log(users);
  } catch (error) {
    console.error('Error fetching users:', error);
  }
};

// 2. POST Request
const createUser = async (userData) => {
  try {
    const newUser = await post('https://api.example.com/users', userData);
    console.log('User created:', newUser);
  } catch (error) {
    console.error('Error creating user:', error);
  }
};

// 3. PUT/UPDATE Request
const updateUser = async (userId, userData) => {
  try {
    const updatedUser = await put(`https://api.example.com/users/${userId}`, userData);
    console.log('User updated:', updatedUser);
  } catch (error) {
    console.error('Error updating user:', error);
  }
};

// 4. DELETE Request
const deleteUser = async (userId) => {
  try {
    await deleteRequest(`https://api.example.com/users/${userId}`);
    console.log('User deleted');
  } catch (error) {
    console.error('Error deleting user:', error);
  }
};

// 5. Using the generic baseRequest function
const customRequest = async () => {
  try {
    const result = await baseRequest('POST', 'https://api.example.com/custom', {
      name: 'Test',
      email: 'test@example.com'
    });
    console.log(result);
  } catch (error) {
    console.error('Error in custom request:', error);
  }
};

// 6. With additional configuration (headers, params, etc.)
const requestWithConfig = async () => {
  try {
    const result = await get('https://api.example.com/data', {
      headers: {
        'Custom-Header': 'value'
      },
      params: {
        page: 1,
        limit: 10
      }
    });
    console.log(result);
  } catch (error) {
    console.error('Error:', error);
  }
};
*/
